"use client";

import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import Button, { ButtonVariant } from "@/app/(components)/generics/Button/Button";
import IconArrowDown from "@/app/(components)/generics/Icons/IconArrowDown";
import IconArrowUp from "@/app/(components)/generics/Icons/IconArrowUp";
import IconPlus from "@/app/(components)/generics/Icons/IconPlus";
import IconTrash from "@/app/(components)/generics/Icons/IconTrash";
import { Input } from "@/app/(components)/generics/inputs/default/Input/Input";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import TabbedContent from "@/app/(components)/generics/TabbedContent/TabbedContent";
import {
  EditPanelStore,
  EditPanelType,
} from "@/app/(components)/specifics/configurator/Configurator/ui/EditPanel/EditPanelStore";
import {
  ViewerStore,
  ZoneId,
} from "@/app/(components)/specifics/configurator/Configurator/ui/Viewer/ViewerStore";
import { deleteZonePreset, getZonePresets } from "@/app/actions/zone-preset/action";
import { ConfiguratorStore } from "@/app/stores/ConfiguratorStore";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { T } from "@/app/translations/Translations";
import { PublicZonePreset } from "@/app/types/ZonePreset";
import { UColor } from "@/app/utils/UColor";
import { UStyles } from "@/app/utils/UStyles";
import { ConfiguratorQuery } from "@/app/vars/Routes";
import styles from "./AddZone.module.css";

type Props = { lang: string };

export default function AddZone(props: Props) {
  const { lang } = props;

  const { closeModal } = ModalStore();

  const isModelMode = ConfiguratorStore((state) => state.isModelMode);
  const searchParams = useSearchParams();

  const [parent, setParent] = useState<ZoneId.Top | ZoneId.Base>(ZoneId.Top);
  const [data, setData] = useState({ name: "" });
  const [presets, setPresets] = useState<PublicZonePreset[]>([]);
  const [loading, setLoading] = useState(true);

  // Global presets come from the public models (managed by the admin there), so
  // they are not deletable here; only personal presets can be removed.
  const canDelete = (preset: PublicZonePreset) => !preset.isGlobal;

  const globalPresets = presets.filter((preset) => preset.isGlobal);
  const myPresets = presets.filter((preset) => !preset.isGlobal);

  const renderPreset = (preset: PublicZonePreset) => (
    <div key={preset.uuid} className={styles.presetRow}>
      <button type="button" className={styles.presetBtn} onClick={() => handleAddPreset(preset)}>
        <span
          className={styles.presetDot}
          style={{ backgroundColor: colorFor(preset.config.parentZone) }}
        />
        <span className={styles.presetName}>{preset.name}</span>
        <span className={styles.presetAdd}>
          <IconPlus />
        </span>
      </button>
      {canDelete(preset) && (
        <button
          type="button"
          className={styles.presetDelete}
          title={T(TKey.DELETE_ZONE_PRESET, lang) as string}
          onClick={() => handleDeletePreset(preset.uuid)}
        >
          <IconTrash />
        </button>
      )}
    </div>
  );

  const fetchPresets = useCallback(async () => {
    setLoading(true);
    setPresets(await getZonePresets({ pairUuid: searchParams.get(ConfiguratorQuery.PAIR) }));
    setLoading(false);
  }, [searchParams]);

  useEffect(() => {
    fetchPresets();
  }, [fetchPresets]);

  const colorFor = (zoneParent: ZoneId.Top | ZoneId.Base) =>
    zoneParent === ZoneId.Top ? UColor.ZONES.top : UColor.ZONES.base;

  const handleAddPreset = (preset: PublicZonePreset) => {
    const { config } = preset;
    const id = ViewerStore.getState().addActiveZone(
      config.parentZone,
      colorFor(config.parentZone),
      preset.name,
    );
    ViewerStore.getState().updateActiveZone(id, {
      shape: config.shape,
      width: config.width,
      length: config.length,
      posX: config.posX,
      posY: config.posY,
      offsetZ: config.offsetZ,
      rotX: config.rotX,
      rotY: config.rotY,
      kind: config.kind,
      wedgeAngle: config.wedgeAngle,
    });
    EditPanelStore.getState().openPanel(EditPanelType.Dimensions);
    closeModal();
  };

  const handleDeletePreset = async (uuid: string) => {
    await deleteZonePreset(uuid);
    await fetchPresets();
  };

  const handleAdd = () => {
    ViewerStore.getState().addActiveZone(parent, colorFor(parent), data.name);
    EditPanelStore.getState().openPanel(EditPanelType.Dimensions);
    closeModal();
  };

  const presetsContent = (
    <div className={styles.tabBody}>
      {loading ? (
        <PointsLoader dark delay />
      ) : presets.length === 0 ? (
        <p className={styles.empty}>{T(TKey.ZONE_PRESETS_EMPTY, lang)}</p>
      ) : (
        <div className={styles.groups}>
          {myPresets.length > 0 && (
            <div className={styles.group}>
              <span className={styles.groupLabel}>{T(TKey.ZONE_PRESETS_MINE, lang)}</span>
              <div className={styles.presets}>{myPresets.map(renderPreset)}</div>
            </div>
          )}
          {globalPresets.length > 0 && (
            <div className={styles.group}>
              <span className={styles.groupLabel}>{T(TKey.ZONE_PRESETS_GLOBAL, lang)}</span>
              <div className={styles.presets}>{globalPresets.map(renderPreset)}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const customContent = (
    <div className={styles.tabBody}>
      <Input
        data={data}
        dataKey="name"
        label={T(TKey.ZONE_NAME, lang) as string}
        lang={lang}
        placeholder={T(TKey.ZONE_NAME, lang) as string}
        onChange={(d) => setData({ ...d })}
      />

      <div className={styles.choice}>
        <button
          type="button"
          className={UStyles.classNames(
            styles.choiceBtn,
            parent === ZoneId.Top ? styles.choiceBtnActive : "",
          )}
          onClick={() => setParent(ZoneId.Top)}
        >
          <IconArrowUp size={18} />
          <span>{T(TKey.ZONE_TOP, lang)}</span>
        </button>
        <button
          type="button"
          className={UStyles.classNames(
            styles.choiceBtn,
            parent === ZoneId.Base ? styles.choiceBtnActive : "",
          )}
          onClick={() => setParent(ZoneId.Base)}
        >
          <IconArrowDown size={18} />
          <span>{T(TKey.ZONE_BASE, lang)}</span>
        </button>
      </div>

      <div className={styles.actions}>
        <Button variant={ButtonVariant.SECONDARY} onClick={closeModal}>
          {T(TKey.CANCEL, lang)}
        </Button>
        <Button variant={ButtonVariant.PRIMARY} onClick={handleAdd}>
          {T(TKey.ADD_ZONE, lang)}
        </Button>
      </div>
    </div>
  );

  if (isModelMode) {
    return <div className={styles.container}>{customContent}</div>;
  }

  return (
    <div className={styles.container}>
      <TabbedContent
        hideContentPadding
        items={[
          { id: "presets", title: T(TKey.ZONE_PRESETS, lang) as string, content: presetsContent },
          { id: "custom", title: T(TKey.ZONE_CUSTOM, lang) as string, content: customContent },
        ]}
      />
    </div>
  );
}
